import { APP_CONFIG } from 'appConfig';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import { Theme } from '@material-ui/core/styles/createTheme';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CachedIcon from '@material-ui/icons/Cached';
import CheckIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';

import BucketTabSection from 'components/Bucket/BucketTabSection';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import PieGraph from 'components/Graph/PieGraph';
import BenchmarkIcon from 'components/Icons/BenchmarkIcon';
import CCIcon from 'components/Icons/CCIcon';
import CO2Icon from 'components/Icons/CO2Icon';
import PortfolioTableCurrent from 'components/PortfolioTables/PortfolioTableCurrent';
import Info from 'components/Tooltip/Info';
import Tooltip from 'components/Tooltip/Tooltip';
import compose from 'helpers/compose';
import {
  fractionToPercent,
  isoDateTimeToLabelZoned,
  translatePortfolioCode,
  translatePortfolioType,
} from 'helpers/helpers';
import { Permission } from 'helpers/permissions';

import { Currency, EdwardColors } from 'enums';
import { UserStoreKC } from 'stores/userStoreKC';
import { PortfolioType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import BucketUpdate from './BucketUpdate';
import PortfolioEditor from './PortfolioEditor/PortfolioEditor';
import useStyles from './styles';

const blackRock = require('assets/icons/ishares_br.png');

interface OwnProps {
  accountId: string;
  currency: Currency;
  detailMode?: boolean;
  readOnly?: boolean;
  brief?: boolean;
  userMode?: boolean;
  rebalancingTimeTestDisabled?: boolean;
  portfolioUpdateEnabled: boolean;
}

interface Stores {
  store: WorkflowPlanStore;
  userStore: UserStoreKC;
}

type Props = OwnProps & Stores;

const BucketSection: React.FC<Props> = ({
  store,
  accountId,
  currency,
  detailMode,
  readOnly,
  brief,
  userStore,
  userMode,
  rebalancingTimeTestDisabled,
  portfolioUpdateEnabled,
}) => {
  const { t } = useTranslation();
  const { activatedBucket, bucketTab, pie, activeBuckets, activatedBucketType, handleRiskChange } = store;
  const setActivatedBucketType = React.useCallback(
    (activateBucket) => {
      store.activatedBucketType = activateBucket;
      setPortfolioCode(`${store.activatedBucket?.portfolio.code.type},${store.activatedBucket?.portfolio.code.code}`);
    },
    [store],
  );
  const [viewDetail, setViewDetail] = useState(false);
  const [portfolioCode, setPortfolioCode] = useState(
    `${activatedBucket?.portfolio.code.type},${activatedBucket?.portfolio.code.code}`,
  );
  const [prevPortfolioCode, setPrevPortfolioCode] = useState(
    `${activatedBucket?.portfolio.code.type},${activatedBucket?.portfolio.code.code}`,
  );
  const [esgMismatch, setEsgMismatch] = useState(false);
  const [portfolioEditorOpen, setPortfolioEditorOpen] = useState(false);
  const [oneTimeRebalanceOpen, setOneTimeRebalanceOpen] = useState(false);
  const [rebalanceBucket, setRebalanceBucket] = useState(false);
  const [existingIndPortfolioDialog, setExistingIndPortfolioDialog] = useState(false);
  const [bucketUpdateOpen, setBucketUpdateOpen] = useState(false);

  const tablet = useMediaQuery('(max-width:800px)', { noSsr: true });
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });

  const currencyHedgingEnabled = userStore.checkPermissions(Permission.ACCOUNT_HEDGING) && currency === Currency.CZK;
  const rebalancingEnabled = userStore.checkPermissions(Permission.ACCOUNT_REBALANCING);
  const indPortfolio = userStore.checkPermissions(Permission.ACCOUNT_IND_PORTFOLIO);
  const classes = useStyles({ detailMode, indPortfolio });
  const portfolioType = activatedBucket?.portfolio.code.type as PortfolioType;
  const showDrift = activatedBucket?.drift !== undefined && (activatedBucket?.balance.value ?? 0) > 0;

  const handleEditPortfolioClick = () => {
    const isIndPortfolio = portfolioType === PortfolioType.IND;
    const hasIndPortfolio = activatedBucket?.portfolio.options.individualPortfolio !== undefined;
    const editPortfolioWithWarning = !isIndPortfolio && hasIndPortfolio;

    if (editPortfolioWithWarning) {
      setExistingIndPortfolioDialog(true);
    }
    else {
      setPortfolioEditorOpen(!portfolioEditorOpen);
    }
  };

  const showPdfLink = () => {
    return (
      activatedBucket &&
      activatedBucket?.portfolio.code.profile !== '1' &&
      (portfolioType === PortfolioType.STD ||
        portfolioType === PortfolioType.ESG ||
        portfolioType === PortfolioType.BAS)
    );
  };

  const pdfLink =
    showPdfLink() &&
    `${APP_CONFIG.extWebsite}${t('portfolio_pdf_url')}${t(
      `portfolio_pdf_url_${portfolioType}_MP${activatedBucket?.portfolio.code.profile}`,
    )}${t('portfolio_pdf_fileSuffix')}`;

  useEffect(() => {
    setPortfolioCode(`${activatedBucket?.portfolio.code.type},${activatedBucket?.portfolio.code.code}`);
    setPrevPortfolioCode(`${activatedBucket?.portfolio.code.type},${activatedBucket?.portfolio.code.code}`);
  }, [activatedBucket?.portfolio.code.type, activatedBucket?.portfolio.code.code]);

  return (
    <>
      <BucketTabSection
        buckets={activeBuckets}
        activatedBucketType={viewDetail ? activatedBucketType : ''}
        onBucketActivate={setActivatedBucketType}
        accountId={accountId}
        handleRiskChange={handleRiskChange}
        currency={currency}
        readOnly={readOnly}
        showAdditionalInfo={brief}
        viewDetail={setViewDetail}
        mobile={mobile}
        tablet={tablet}
        store={store}
        currencyHedgingEnabled={currencyHedgingEnabled}
        rebalancingEnabled={rebalancingEnabled}
        rebalancingTimeTestDisabled={rebalancingTimeTestDisabled}
        userMode={userMode}
        detailMode={detailMode}
      >
        <>
          {bucketTab === 0 && viewDetail && (
            <Grid
              container
              item
              xs={12}
              spacing={0}
              style={{ backgroundColor: '#fff', marginTop: '-1px', zIndex: 1 }}
              alignItems="center"
              className="DETAIL"
            >
              <Grid container item xs={12} spacing={0} className={classes.chartArea}>
                <Grid item xs={12} lg={4} />
                <Grid item xs={12} sm={8} md={6} lg={4} style={{ padding: '16px', textAlign: 'center' }}>
                  <Typography variant="h2" style={{ marginBottom: '24px' }}>
                    {t('general_detail')} <strong>{t(`${activatedBucket?.title}.detail`)}</strong>
                  </Typography>
                  {!userMode && (
                    <TextField
                      select={true}
                      value={portfolioCode}
                      label={t('bucket_selectInvestmentStrategy')}
                      onChange={(e) => {
                        if (e.target.value.substring(0, 3) !== PortfolioType.ESG && store.data?.esg) {
                          setEsgMismatch(true);
                          setPortfolioCode(e.target.value);
                        } else {
                          setPortfolioCode(e.target.value);
                          setPrevPortfolioCode(e.target.value);
                          if (detailMode) {
                            setOneTimeRebalanceOpen(true);
                          } else {
                            store.setPortfolioCode(e.target.value, activatedBucket?.type);
                          }
                        }
                      }}
                      disabled={readOnly}
                      style={{ maxWidth: '340px', textAlign: 'left', marginBottom: '8px' }}
                    >
                      {activatedBucket &&
                        activatedBucket.portfolio.options.code.map((code) => (
                          <MenuItem key={code.code} value={`${code.type},${code.code}`}>
                            {translatePortfolioType(code.code)}: {translatePortfolioCode(code)}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                  {activatedBucket?.portfolio.lastUpdate && (
                    <>
                      <br />
                      <strong>
                        {t('general_lastUpdate')}: {isoDateTimeToLabelZoned(activatedBucket.portfolio.lastUpdate)}
                      </strong>
                      {!readOnly && (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => {
                            setBucketUpdateOpen(true);
                          }}
                          disabled={!portfolioUpdateEnabled}
                          style={{ marginLeft: '16px' }}
                        >
                          {t('accountDetail_portfolio_change_viewShort')}
                        </Button>
                      )}
                    </>
                  )}
                </Grid>

                <Grid item xs={12} sm={4} md={6} lg={4} style={{ padding: '16px', textAlign: 'right' }}>
                  {indPortfolio && !readOnly && (
                    <>
                      <Button
                        onClick={handleEditPortfolioClick}
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        className={classes.editPortfolio}
                      >
                        {t('accountPlan_editPortfolio')}
                      </Button>
                      <ConfirmDialog
                        disableClose
                        btnConfirmText="general_modify"
                        isOpen={existingIndPortfolioDialog}
                        close={() => setExistingIndPortfolioDialog(false)}
                        confirm={() => {
                          setExistingIndPortfolioDialog(false);
                          setPortfolioEditorOpen(true);
                        }}
                        noHeader
                      >
                        <>
                          <Typography variant="h3" style={{ textAlign: 'center' }}>
                            {t('general_warning')}
                          </Typography>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: t(`accountPlan_editExistingPortfolio`, {
                                interpolation: { escapeValue: false },
                                title: activatedBucket?.portfolio.options.individualPortfolio?.title,
                                risk: activatedBucket?.portfolio.options.individualPortfolio?.calculatedProfile,
                              }),
                            }}
                          />
                        </>
                      </ConfirmDialog>

                      {portfolioEditorOpen && activatedBucketType && (
                        <PortfolioEditor
                          planStore={store}
                          id={accountId}
                          isOpen={portfolioEditorOpen}
                          handleOpen={setPortfolioEditorOpen}
                          bucket={activatedBucketType}
                          detailMode={!!detailMode}
                          currency={currency}
                          rebalancing={activatedBucket?.automaticRebalancing}
                          originalPortfolioCode={activatedBucket?.portfolio.code.code}
                        />
                      )}
                    </>
                  )}
                </Grid>

                {activatedBucket?.portfolio.code.type === PortfolioType.ESG &&
                  activatedBucket?.portfolio.code.profile !== '1' && (
                    <Grid item xs={12} sm={12} md={4} lg={2} className={classes.esg}>
                      <div>
                        {activatedBucket?.portfolio.msciRating && (
                          <div>
                            <BenchmarkIcon defaultStroke />
                            <span>
                              {t('esg_msciRating')}: {fractionToPercent(activatedBucket.portfolio.msciRating)}
                              <Info
                                content={
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: t(`tooltip_esgInfo`, {
                                        interpolation: { escapeValue: false },
                                      }),
                                    }}
                                  />
                                }
                              />
                            </span>
                          </div>
                        )}
                        {activatedBucket?.portfolio.carbon && (
                          <div>
                            <CO2Icon />
                            <span>
                              {t('esg_carbon')}: {fractionToPercent(activatedBucket.portfolio.carbon)}
                            </span>
                          </div>
                        )}
                        <div>
                          <CCIcon /> <span>{t('esg_cc')}: 0 %</span>
                        </div>
                        <div className="blackrock">
                          <img src={blackRock.default} alt="iShares BlackRock" />
                          <span> {t('esg_blackrock')}</span>
                        </div>
                      </div>
                    </Grid>
                  )}
                <Grid container item xs={12} justifyContent="center" spacing={0} className={classes.chart}>
                  {pie.data ? (
                    <div className={classes.pieWrapper}>
                      <PieGraph
                        data={pie}
                        settings={{
                          chart: {
                            spacingTop: 10,
                            spacingLeft: 10,
                          },
                        }}
                      />
                    </div>
                  ) : null}
                  {pdfLink && (
                    <Button startIcon={<GetAppIcon />} href={pdfLink}>
                      {t('portfolio_pdf')}
                    </Button>
                  )}
                </Grid>
                <Grid container item xs={12} spacing={0} className={classes.table}>
                  <Grid container item xs={12} spacing={0} className={classes.bucketMisc}>
                    {showDrift && (
                      <Grid item xs={12} sm={6} style={{ textAlign: 'left' }}>
                        <span style={{ fontSize: '18px', fontWeight: 800 }}>{t('bucket_drift')}</span>
                        <Info
                          content={
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t(`bucket_drift_tooltip`, {
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                          }
                          heading
                        />
                        <span className={classes.drift}>
                          <span
                            className={
                              activatedBucket!.drift! > activatedBucket!.options.driftThreshold[1]
                                ? 'HIGH'
                                : activatedBucket!.drift! > activatedBucket!.options.driftThreshold[0]
                                  ? 'MID'
                                  : 'LOW'
                            }
                          >
                            {fractionToPercent(activatedBucket?.drift!)}
                          </span>
                        </span>
                      </Grid>
                    )}
                    {detailMode && !userMode && !brief && (
                      <Grid item xs={12} sm={showDrift ? 6 : 12} style={{ textAlign: 'right' }}>
                        <Tooltip title={readOnly ? t('accountDetail_readOnlyInfo') : ''}>
                          <span>
                            <Button
                              startIcon={activatedBucket?.oneTimeRebalance ? <CheckIcon /> : <CachedIcon />}
                              color={activatedBucket?.oneTimeRebalance ? 'primary' : 'default'}
                              variant="contained"
                              onClick={() => setRebalanceBucket(true)}
                              style={{ margin: '0 8px' }}
                              disabled={readOnly}
                            >
                              {t(
                                `accountDetail_plan_bucketRebalance${activatedBucket?.oneTimeRebalance ? '_active' : ''
                                }`,
                              )}
                            </Button>
                          </span>
                        </Tooltip>
                        <Info
                          content={
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t(`accountDetail_plan_bucketRebalance_active_tooltip`, {
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                          }
                          inline
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={0} className={classes.table}>
                  <PortfolioTableCurrent
                    detailMode={detailMode}
                    userMode={userMode}
                    assetCategories={activatedBucket?.portfolio?.assetCategories}
                    terAverage={activatedBucket?.portfolio?.terAverage}
                    distributionTotal={activatedBucket?.portfolio?.distributionTotal}
                    savingIsins={store.data?.options.specialAssets.savings}
                    drift={activatedBucket?.balance.value !== 0 ? activatedBucket?.instrumentDrift : undefined}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {bucketUpdateOpen && <BucketUpdate store={store} handleClose={() => setBucketUpdateOpen(false)} />}

          <ConfirmDialog
            title={null}
            btnCloseText={t('general_back')}
            btnConfirmText={t('general_continue')}
            isOpen={esgMismatch}
            close={() => {
              setEsgMismatch(false);
              setPortfolioCode(prevPortfolioCode);
            }}
            confirm={() => {
              setEsgMismatch(false);
              setPrevPortfolioCode(portfolioCode);
              if (detailMode) {
                setOneTimeRebalanceOpen(true);
              } else {
                store.setPortfolioCode(portfolioCode, activatedBucket?.type);
              }
            }}
            icons
          >
            <Typography style={{ fontSize: '19px', fontWeight: 'bold' }}>{t('esg_mismatchAlert')}</Typography>
            <Typography style={{ fontSize: '19px', fontWeight: 'bold', color: EdwardColors.GRAY_MEDIUM }}>
              {t('esg_mismatchContinue')}
            </Typography>
          </ConfirmDialog>

          {/* TODO: OTR component (3 places) */}
          <ConfirmDialog
            disableClose
            title={`accountDetail_plan_oneTimeRebalancing_title_${activatedBucket?.automaticRebalancing === false ? 'off' : 'on'
              }`}
            btnCloseText="accountDetail_plan_oneTimeRebalancing_dont"
            btnConfirmText="accountDetail_plan_oneTimeRebalancing_do"
            isOpen={oneTimeRebalanceOpen}
            close={async () => {
              store && activatedBucket?.type && (await store.setOneTimeRebalancing(activatedBucket?.type, false));
              setOneTimeRebalanceOpen(false);
              store.setPortfolioCode(portfolioCode, activatedBucket?.type);
            }}
            confirm={async () => {
              store && activatedBucket?.type && (await store.setOneTimeRebalancing(activatedBucket?.type, true));
              setOneTimeRebalanceOpen(false);
              store.setPortfolioCode(portfolioCode, activatedBucket?.type);
            }}
          >
            <p>
              {t(
                `accountDetail_plan_oneTimeRebalancing_auto${activatedBucket?.automaticRebalancing === false ? 'Off' : 'On'
                }_info`,
              )}
            </p>
            <p>
              {t(
                `accountDetail_plan_oneTimeRebalancing_auto${activatedBucket?.automaticRebalancing === false ? 'Off' : 'On'
                }_info2`,
              )}
            </p>
          </ConfirmDialog>

          <ConfirmDialog
            disableClose
            title={'accountDetail_plan_bucketRebalancing'}
            btnCloseText="accountDetail_plan_oneTimeRebalancing_dont"
            btnConfirmText="accountDetail_plan_oneTimeRebalancing_do"
            isOpen={rebalanceBucket}
            close={async () => {
              store && activatedBucket?.type && (await store.setOneTimeRebalancing(activatedBucket?.type, false));
              setRebalanceBucket(false);
            }}
            confirm={async () => {
              store && activatedBucket?.type && (await store.setOneTimeRebalancing(activatedBucket?.type, true));
              setRebalanceBucket(false);
            }}
          >
            <p
              dangerouslySetInnerHTML={{
                __html: t(`accountDetail_plan_bucketRebalancing_info`, {
                  interpolation: { escapeValue: false },
                }),
              }}
            />
          </ConfirmDialog>
        </>
      </BucketTabSection>
    </>
  );
};

export default compose(inject('userStore'), observer)(BucketSection);
